import React, { useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './index.css';
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const InvitationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', city: '', email: '', phone: '',
    lifestyle: '', profession: '', incomeRange: '', industry: '',
    intent: '', termsAccepted: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      // Save to Firestore
      await addDoc(collection(db, 'invitations'), {
        ...formData,
        timestamp: serverTimestamp()
      });

      // Send emails via API (non-blocking — continues even if endpoint doesn't exist yet)
      fetch('https://luxe-backend.netlify.app/luxe-invitation-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          lifestyle: formData.lifestyle,
          profession: formData.profession,
          incomeRange: formData.incomeRange,
          industry: formData.industry,
          intent: formData.intent,
        }),
      }).catch(err => console.warn('Email API not ready:', err));

      setSubmitted(true);
      setTimeout(() => navigate('/'), 5000);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const lifestyleOptions = [
    'Private, quiet escapes',
    'Time with family',
    'Slow, immersive journeys',
    'A balance of all'
  ];

  const professionOptions = ['Working Professional', 'Entrepreneur / Business Owner'];

  const incomeRanges = {
    'Working Professional': ['INR 25–50 Lakhs', 'INR 50–1 Crore', 'INR 1 Crore and above'],
    'Entrepreneur / Business Owner': ['INR 5–15 Crores', 'INR 15–30 Crores', 'INR 30 Crores and above']
  };

  if (submitted) {
    return (
      <div className="inv-page">
        <div className="inv-confirmation">
          <div className="inv-check">✦</div>
          <p className="inv-conf-label">Thank You</p>
          <h1 className="inv-conf-title">You're on our radar.</h1>
          <p className="inv-conf-sub">
            If this feels like something meant for you, it likely is.<br />We'll be in touch.
          </p>
          <p className="inv-conf-note">Returning home in a moment...</p>
          <button className="inv-btn-primary inv-btn-home" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="inv-page">

      {/* Back button */}
      <button className="inv-back-btn" onClick={() => navigate(-1)}>← Back</button>

      {/* Hero heading */}
      <div className="inv-hero">
        <p className="inv-hero-label">By Invitation Only</p>
        <h1 className="inv-hero-title">Seek Invitation</h1>
        <p className="inv-hero-sub">
          A private circle, extended to a select few. Each request is reviewed with care.<br />
          If aligned, our team will reach out personally.
        </p>
      </div>

      {/* Form */}
      <div className="inv-form-section">
        <form className="inv-form" onSubmit={handleSubmit}>

          {/* Personal Introduction */}
          <div className="inv-section">
            <div className="inv-section-header">
              <p className="inv-section-num">01</p>
              <h2 className="inv-section-title">Tell us who you are</h2>
              <p className="inv-section-sub">A brief introduction, to begin the conversation.</p>
            </div>
            <div className="inv-fields-grid">
              <div className="inv-field">
                <label className="inv-field-label">Full Name *</label>
                <input className="inv-input" type="text" placeholder="Your full name" value={formData.name} onChange={e => update('name', e.target.value)} required />
              </div>
              <div className="inv-field">
                <label className="inv-field-label">City of Residence</label>
                <input className="inv-input" type="text" placeholder="Your city" value={formData.city} onChange={e => update('city', e.target.value)} />
              </div>
              <div className="inv-field">
                <label className="inv-field-label">Email Address *</label>
                <input className="inv-input" type="email" placeholder="your@email.com" value={formData.email} onChange={e => update('email', e.target.value)} required />
              </div>
              <div className="inv-field">
                <label className="inv-field-label">Contact Number</label>
                <PhoneInput
                  international
                  defaultCountry="IN"
                  value={formData.phone}
                  onChange={val => update('phone', val)}
                  className="inv-phone"
                  placeholder="+91 00000 00000"
                />
              </div>
            </div>
          </div>

          <div className="inv-divider" />

          {/* Lifestyle */}
          <div className="inv-section">
            <div className="inv-section-header">
              <p className="inv-section-num">02</p>
              <h2 className="inv-section-title">How do you choose to experience the world?</h2>
              <p className="inv-section-sub">Select what resonates most with you.</p>
            </div>
            <div className="inv-pills">
              {lifestyleOptions.map(opt => (
                <button type="button" key={opt} className={`inv-pill ${formData.lifestyle === opt ? 'active' : ''}`} onClick={() => update('lifestyle', opt)}>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="inv-divider" />

          {/* Professional */}
          <div className="inv-section">
            <div className="inv-section-header">
              <p className="inv-section-num">03</p>
              <h2 className="inv-section-title">A note on what you do</h2>
              <p className="inv-section-sub">Select what best describes you.</p>
            </div>
            <div className="inv-pills">
              {professionOptions.map(opt => (
                <button type="button" key={opt} className={`inv-pill ${formData.profession === opt ? 'active' : ''}`} onClick={() => update('profession', opt)}>
                  {opt}
                </button>
              ))}
            </div>
            {formData.profession && (
              <div className="inv-reveal">
                <div className="inv-fields-grid">
                  <div className="inv-field">
                    <label className="inv-field-label">
                      {formData.profession === 'Working Professional' ? 'Annual Income Range' : 'Annual Turnover Range'}
                    </label>
                    <div className="inv-pills inv-pills--sm">
                      {incomeRanges[formData.profession].map(range => (
                        <button type="button" key={range} className={`inv-pill inv-pill--sm ${formData.incomeRange === range ? 'active' : ''}`} onClick={() => update('incomeRange', range)}>
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="inv-field">
                    <label className="inv-field-label">
                      {formData.profession === 'Working Professional' ? 'Industry' : 'Nature of Business'}
                    </label>
                    <input className="inv-input" type="text" placeholder="e.g. Finance, Technology, Real Estate..." value={formData.industry} onChange={e => update('industry', e.target.value)} />
                  </div>
                </div>
                <p className="inv-note">Shared only to ensure alignment with the circle.</p>
              </div>
            )}
          </div>

          <div className="inv-divider" />

          {/* Intent */}
          <div className="inv-section">
            <div className="inv-section-header">
              <p className="inv-section-num">04</p>
              <h2 className="inv-section-title">What draws you here?</h2>
              <p className="inv-section-sub">A line or two, if you wish.</p>
            </div>
            <textarea className="inv-textarea" rows="4" placeholder="Share what resonates with you about Luxe Unbounded..." value={formData.intent} onChange={e => update('intent', e.target.value)} />
          </div>

          <div className="inv-divider" />

          {/* Submit */}
          <div className="inv-section inv-section--submit">
            {error && <p className="inv-error">{error}</p>}
            <button type="submit" className="inv-btn-primary" disabled={submitting || !formData.name || !formData.email}>
              {submitting ? 'Submitting...' : 'Submit for Consideration'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default InvitationForm;
